import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

const ConfirmSignUp = ({ email, onConfirmSuccess, onBackToLogin }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      onConfirmSuccess();
    } catch (err) {
      console.error('Confirm error:', err);
      if (err.name === 'CodeMismatchException') {
        setError(t('auth.error_invalid_code'));
      } else if (err.name === 'ExpiredCodeException') {
        setError(t('auth.error_code_expired'));
      } else {
        setError(t('auth.error_confirm_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setResending(true);

    try {
      await resendSignUpCode({
        username: email,
      });
      setSuccess(t('auth.code_resent'));
    } catch (err) {
      console.error('Resend error:', err);
      setError(t('auth.error_resend_failed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="card-elevated p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <Logo size={64} className="text-primary" animated />
            </div>
            <h1 className="text-2xl font-bold text-primary mt-4">
              {t('auth.verify_email')}
            </h1>
            <p className="text-sm text-text-muted mt-2">
              {t('auth.verification_code_sent', { email })}
            </p>
          </div>

          {/* Confirm Form */}
          <form onSubmit={handleConfirm} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-text-muted mb-1">
                {t('auth.verification_code')}
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                autoFocus
                disabled={loading}
                placeholder={t('auth.code_placeholder')}
                className="input text-center text-2xl tracking-widest"
                maxLength={6}
              />
            </div>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            {success && (
              <Alert severity="success">{success}</Alert>
            )}

            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  {t('auth.confirming')}
                </>
              ) : (
                t('auth.confirm')
              )}
            </button>
          </form>

          {/* Resend Code */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resending || loading}
              className="text-sm text-primary hover:underline cursor-pointer"
            >
              {resending ? t('auth.resending') : t('auth.resend_code')}
            </button>
          </div>

          {/* Back to Login */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={onBackToLogin}
              disabled={loading}
              className="text-sm text-text-muted hover:underline cursor-pointer"
            >
              {t('auth.back_to_login')}
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-text-light opacity-70">
              © 2025 Holocron
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSignUp;
